// Painted signage drawn on canvases: hoarding boards, hanging banners, the finish banner,
// stall headers and the podium. Original short copy and simple motifs only (ui/index.md),
// in the broadcast palette: plum, maroon, ivory and gold.
import { SRGBColorSpace, type CanvasTexture } from 'three';
import { PALETTE, SIGN_FONT, canvasTexture, drawCrown, drawPaw, rng } from './util';

/** Fabric weave and soft wear over a sign, so it does not read as flat plastic. */
function fabric(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, seed: number, strength = 1): void {
  const r = rng(seed);
  g.save();
  g.globalAlpha = 0.05 * strength;
  g.fillStyle = '#000';
  for (let i = y; i < y + h; i += 3) g.fillRect(x, i, w, 1);
  g.globalAlpha = 0.035 * strength;
  for (let i = x; i < x + w; i += 3) g.fillRect(i, y, 1, h);
  for (let i = 0; i < (w * h) / 900; i++) {
    g.globalAlpha = r() * 0.06 * strength;
    g.fillStyle = r() < 0.5 ? '#000' : '#fff';
    g.beginPath(); g.ellipse(x + r() * w, y + r() * h, 4 + r() * 30, 2 + r() * 10, r() * 3, 0, Math.PI * 2); g.fill();
  }
  // Darkened edges where fabric meets the frame.
  const grad = g.createLinearGradient(x, y, x, y + h);
  grad.addColorStop(0, 'rgba(0,0,0,0.25)'); grad.addColorStop(0.08, 'rgba(0,0,0,0)');
  grad.addColorStop(0.92, 'rgba(0,0,0,0)'); grad.addColorStop(1, 'rgba(0,0,0,0.3)');
  g.globalAlpha = strength; g.fillStyle = grad; g.fillRect(x, y, w, h);
  g.restore();
}

function centredText(g: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, colour: string, maxW?: number): void {
  g.save();
  g.font = `900 ${size}px ${SIGN_FONT}`;
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillStyle = 'rgba(0,0,0,0.35)';
  g.fillText(text, x + size * 0.04, y + size * 0.06, maxW);
  g.fillStyle = colour;
  g.fillText(text, x, y, maxW);
  g.restore();
}

function goldFrame(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, inset: number, line: number): void {
  g.save(); g.strokeStyle = PALETTE.gold; g.lineWidth = line; g.strokeRect(x + inset, y + inset, w - inset * 2, h - inset * 2); g.restore();
}

export const HOARDING_DESIGNS = 12;

/** Hoarding atlas: twelve 1024×256 designs stacked vertically. */
export function hoardingAtlas(): CanvasTexture {
  return canvasTexture(1024, 256 * HOARDING_DESIGNS, (g) => {
    const W = 1024, H = 256;
    const designs: ((y: number) => void)[] = [
      (y) => { // Wordmark with crowns.
        g.fillStyle = PALETTE.plum; g.fillRect(0, y, W, H);
        goldFrame(g, 0, y, W, H, 14, 6);
        drawCrown(g, 150, y + H / 2, 110, PALETTE.gold); drawCrown(g, W - 150, y + H / 2, 110, PALETTE.gold);
        centredText(g, 'CAT DERBY', W / 2, y + H / 2 + 6, 120, PALETTE.gold, 620);
      },
      (y) => { // Row of paws.
        g.fillStyle = PALETTE.maroon; g.fillRect(0, y, W, H);
        for (let i = 0; i < 6; i++) drawPaw(g, 90 + i * 170, y + H / 2 + (i % 2 ? 22 : -22), 120, i % 2 ? PALETTE.gold : PALETTE.ivory);
      },
      (y) => {
        g.fillStyle = PALETTE.wine; g.fillRect(0, y, W, H);
        goldFrame(g, 0, y, W, H, 12, 4);
        centredText(g, 'PURR POWER', W / 2, y + H / 2 + 6, 112, PALETTE.ivory, 860);
      },
      (y) => {
        g.fillStyle = PALETTE.plum; g.fillRect(0, y, W, H);
        drawPaw(g, 110, y + H / 2, 120, PALETTE.gold); drawPaw(g, W - 110, y + H / 2, 120, PALETTE.gold);
        centredText(g, 'NINE LIVES', W / 2, y + H / 2 - 40, 76, PALETTE.ivory, 680);
        centredText(g, 'ONE RACE', W / 2, y + H / 2 + 52, 76, PALETTE.gold, 680);
      },
      (y) => {
        g.fillStyle = PALETTE.maroonDeep; g.fillRect(0, y, W, H);
        goldFrame(g, 0, y, W, H, 14, 6);
        centredText(g, 'GO KITTIES!', W / 2, y + H / 2 + 6, 118, PALETTE.gold, 860);
      },
      (y) => { // Chequer ends with the course length.
        g.fillStyle = PALETTE.plum; g.fillRect(0, y, W, H);
        for (const x0 of [0, W - 192]) for (let cy = 0; cy < 4; cy++) for (let cx = 0; cx < 3; cx++) {
          g.fillStyle = (cx + cy) % 2 ? '#141012' : PALETTE.ivory; g.fillRect(x0 + cx * 64, y + cy * 64, 64, 64);
        }
        centredText(g, '600 M STRAIGHT', W / 2, y + H / 2 + 6, 84, PALETTE.ivory, 600);
      },
      (y) => { // Race food: a kibble bowl.
        g.fillStyle = PALETTE.maroon; g.fillRect(0, y, W, H);
        goldFrame(g, 0, y, W, H, 12, 4);
        g.fillStyle = PALETTE.gold;
        g.beginPath(); g.ellipse(150, y + 150, 90, 24, 0, 0, Math.PI * 2); g.fill();
        g.beginPath(); g.moveTo(60, y + 150); g.quadraticCurveTo(150, y + 250, 240, y + 150); g.fill();
        g.fillStyle = PALETTE.ivory;
        for (let k = 0; k < 7; k++) { g.beginPath(); g.arc(100 + k * 17, y + 138 - (k % 3) * 8, 11, 0, Math.PI * 2); g.fill(); }
        centredText(g, 'RACE KIBBLE', W / 2 + 120, y + H / 2 - 30, 104, PALETTE.ivory, 640);
        centredText(g, 'FUEL FOR THE FINAL STRAIGHT', W / 2 + 120, y + H / 2 + 64, 40, PALETTE.gold, 640);
      },
      (y) => {
        g.fillStyle = PALETTE.plum; g.fillRect(0, y, W, H);
        goldFrame(g, 0, y, W, H, 14, 6);
        centredText(g, 'TUNA BOOST', W / 2, y + H / 2 - 30, 112, PALETTE.gold, 820);
        centredText(g, 'THE GALLOP IN A CAN', W / 2, y + H / 2 + 66, 48, PALETTE.ivory, 760);
      },
      (y) => { // Racing harnesses.
        g.fillStyle = PALETTE.wine; g.fillRect(0, y, W, H);
        drawPaw(g, 120, y + H / 2, 110, PALETTE.ivory); drawPaw(g, W - 120, y + H / 2, 110, PALETTE.ivory);
        centredText(g, 'SWIFTSTRAP', W / 2, y + H / 2 - 30, 100, PALETTE.ivory, 680);
        centredText(g, 'RACE HARNESSES · NO TUGGING', W / 2, y + H / 2 + 62, 40, PALETTE.gold, 680);
      },
      (y) => {
        g.fillStyle = PALETTE.maroonDeep; g.fillRect(0, y, W, H);
        goldFrame(g, 0, y, W, H, 12, 4);
        centredText(g, 'PAWSOLES', W / 2, y + H / 2 - 30, 112, PALETTE.ivory, 800);
        centredText(g, 'GRIP FOR EVERY CLAW', W / 2, y + H / 2 + 66, 48, PALETTE.gold, 760);
      },
      (y) => {
        g.fillStyle = PALETTE.plum; g.fillRect(0, y, W, H);
        drawCrown(g, 130, y + H / 2, 100, PALETTE.gold); drawCrown(g, W - 130, y + H / 2, 100, PALETTE.gold);
        centredText(g, 'WHISKER WATER', W / 2, y + H / 2 - 30, 96, PALETTE.ivory, 700);
        centredText(g, 'STAY SLEEK, STAY FIRST', W / 2, y + H / 2 + 62, 42, PALETTE.gold, 700);
      },
      (y) => {
        g.fillStyle = PALETTE.maroon; g.fillRect(0, y, W, H);
        goldFrame(g, 0, y, W, H, 14, 6);
        centredText(g, 'NAP & GO', W / 2, y + H / 2 - 30, 112, PALETTE.gold, 800);
        centredText(g, 'CUSHIONS FOR CHAMPIONS', W / 2, y + H / 2 + 66, 46, PALETTE.ivory, 760);
      },
    ];
    designs.forEach((d, i) => { d(i * H); fabric(g, 0, i * H, W, H, 100 + i); });
  });
}

export const BANNER_DESIGNS = 4;

/** Hanging banner atlas: four 256×640 vertical designs side by side, with a swallow-tail cut. */
export function bannerAtlas(): CanvasTexture {
  const t = canvasTexture(256 * BANNER_DESIGNS, 640, (g) => {
    const W = 256, H = 640;
    for (let i = 0; i < BANNER_DESIGNS; i++) {
      const x = i * W;
      g.save();
      g.beginPath();
      g.moveTo(x, 0); g.lineTo(x + W, 0); g.lineTo(x + W, H); g.lineTo(x + W / 2, H - 70); g.lineTo(x, H); g.closePath();
      g.clip();
      g.fillStyle = i % 2 ? PALETTE.wine : PALETTE.maroon; g.fillRect(x, 0, W, H);
      g.strokeStyle = PALETTE.gold; g.lineWidth = 6;
      g.beginPath(); g.moveTo(x + 14, 14); g.lineTo(x + W - 14, 14); g.lineTo(x + W - 14, H - 14); g.lineTo(x + W / 2, H - 82); g.lineTo(x + 14, H - 14); g.closePath(); g.stroke();
      if (i === 0) { drawCrown(g, x + W / 2, 130, 120, PALETTE.gold); centredText(g, 'CAT', x + W / 2, 290, 80, PALETTE.ivory, 210); centredText(g, 'DERBY', x + W / 2, 380, 64, PALETTE.ivory, 210); }
      if (i === 1) { drawPaw(g, x + W / 2, 200, 150, PALETTE.ivory); drawPaw(g, x + W / 2, 400, 90, PALETTE.gold); }
      if (i === 2) { drawPaw(g, x + W / 2, 140, 110, PALETTE.gold); centredText(g, 'MEOW', x + W / 2, 300, 66, PALETTE.ivory, 210); centredText(g, '!', x + W / 2, 400, 90, PALETTE.gold); }
      if (i === 3) { drawCrown(g, x + W / 2, 180, 140, PALETTE.gold); drawPaw(g, x + W / 2, 380, 110, PALETTE.ivory); }
      fabric(g, x, 0, W, H, 300 + i, 1.2);
      g.restore();
    }
  });
  t.colorSpace = SRGBColorSpace;
  return t;
}

/** The finish gantry's main banner, 2048×256: wordmark between chequered flags. */
export function finishBanner(): CanvasTexture {
  return canvasTexture(2048, 288, (g, W, H) => {
    g.fillStyle = PALETTE.plum; g.fillRect(0, 0, W, H);
    const s = H / 4;
    for (const x0 of [0, W - s * 6]) for (let cy = 0; cy < 4; cy++) for (let cx = 0; cx < 6; cx++) {
      g.fillStyle = (cx + cy) % 2 ? '#141012' : PALETTE.ivory; g.fillRect(x0 + cx * s, cy * s, s, s);
    }
    goldFrame(g, s * 6, 0, W - s * 12, H, 14, 8);
    drawCrown(g, W / 2 - 420, H / 2, 120, PALETTE.gold);
    drawCrown(g, W / 2 + 420, H / 2, 120, PALETTE.gold);
    centredText(g, 'CAT DERBY', W / 2, H / 2 - 24, 132, PALETTE.gold, 640);
    centredText(g, 'FINISH', W / 2, H / 2 + 92, 54, PALETTE.ivory, 400);
    fabric(g, 0, 0, W, H, 500);
  });
}

/** Stall headers: six 256×192 plates with the lane number and a paw. */
export function stallHeaders(): CanvasTexture {
  return canvasTexture(256 * 6, 192, (g, _W, H) => {
    for (let i = 0; i < 6; i++) {
      const x = i * 256;
      g.fillStyle = PALETTE.maroon; g.fillRect(x, 0, 256, H);
      goldFrame(g, x, 0, 256, H, 8, 5);
      centredText(g, String(i + 1), x + 128, 76, 104, PALETTE.ivory);
      drawPaw(g, x + 128, 152, 44, PALETTE.gold);
      fabric(g, x, 0, 256, H, 700 + i, 0.8);
    }
  });
}

/** Podium faces: three 256×256 tiles with gold 1, 2, 3 and laurels. */
export function podiumFaces(): CanvasTexture {
  return canvasTexture(768, 256, (g) => {
    ['1', '2', '3'].forEach((n, i) => {
      const x = i * 256;
      g.fillStyle = i === 0 ? '#5a1a2c' : PALETTE.maroon; g.fillRect(x, 0, 256, 256);
      goldFrame(g, x, 0, 256, 256, 10, 6);
      g.save();
      g.strokeStyle = PALETTE.gold; g.lineWidth = 7;
      for (const dir of [-1, 1]) {
        const a0 = dir < 0 ? Math.PI * 0.6 : -Math.PI * 0.4;
        g.beginPath(); g.arc(x + 128, 140, 82, a0, a0 + Math.PI * 0.8); g.stroke();
        for (let k = 0; k < 6; k++) {
          const a = a0 + Math.PI * 0.8 * (k + 0.5) / 6;
          g.fillStyle = PALETTE.gold;
          g.beginPath(); g.ellipse(x + 128 + Math.cos(a) * 82, 140 + Math.sin(a) * 82, 12, 6, a + Math.PI / 2, 0, Math.PI * 2); g.fill();
        }
      }
      g.restore();
      centredText(g, n, x + 128, 132, 140, PALETTE.gold);
      fabric(g, x, 0, 256, 256, 800 + i, 0.6);
    });
  });
}

/** Painted crowd cards for distant stands: rows of cat heads and shoulders in many coats. */
export function crowdCard(): CanvasTexture {
  return canvasTexture(2048, 512, (g, W, H) => {
    const r = rng(42);
    const coats = ['#d9873a', '#f1ece2', '#1d1a1a', '#7d7470', '#c9b089', '#8a5a36', '#e8c58f', '#5b5350', '#b8b2aa', '#2c2522'];
    const rows = 9;
    for (let row = rows - 1; row >= 0; row--) {
      const y = (row + 0.7) * (H / rows);
      for (let x = 10 + r() * 20; x < W; x += 42 + r() * 16) {
        if (r() < 0.06) continue;
        const c = coats[Math.floor(r() * coats.length)]!;
        const s = 15 + r() * 4;
        const up = r() < 0.15 ? 10 : 0;
        g.fillStyle = c;
        g.beginPath(); g.ellipse(x, y + s * 1.2 - up, s * 1.15, s * 1.5, 0, 0, Math.PI * 2); g.fill(); // body
        g.beginPath(); g.arc(x, y - up, s, 0, Math.PI * 2); g.fill(); // head
        g.beginPath(); g.moveTo(x - s * 0.95, y - s * 0.2 - up); g.lineTo(x - s * 0.6, y - s * 1.55 - up); g.lineTo(x - s * 0.1, y - s * 0.75 - up); g.fill();
        g.beginPath(); g.moveTo(x + s * 0.95, y - s * 0.2 - up); g.lineTo(x + s * 0.6, y - s * 1.55 - up); g.lineTo(x + s * 0.1, y - s * 0.75 - up); g.fill();
        if (r() < 0.45) { g.fillStyle = '#f4efe6'; g.beginPath(); g.ellipse(x, y + s * 0.45 - up, s * 0.45, s * 0.38, 0, 0, Math.PI * 2); g.fill(); }
        g.fillStyle = r() < 0.5 ? '#c8d24a' : '#e2b33c';
        g.beginPath(); g.arc(x - s * 0.36, y - s * 0.1 - up, s * 0.13, 0, Math.PI * 2); g.arc(x + s * 0.36, y - s * 0.1 - up, s * 0.13, 0, Math.PI * 2); g.fill();
        if (up) { g.fillStyle = c; g.fillRect(x + s * 0.9, y - s * 1.6, s * 0.35, s * 1.9); } // a raised paw
      }
      // Shade the rows towards the back of the stand.
      g.globalCompositeOperation = 'source-atop';
      g.fillStyle = `rgba(20,10,10,${0.035 * row})`; g.fillRect(0, y - H / rows, W, H / rows * 1.6);
      g.globalCompositeOperation = 'source-over';
    }
  });
}
